'use client';

import type { LogoProps } from '../logo';

import { m } from 'framer-motion';
import { twMerge } from 'tailwind-merge';

import { Logo } from '../logo';

// ----------------------------------------------------------------------

export type AnimateLogoProps = React.ComponentProps<'div'> & {
  className?: string;
  logo?: React.ReactNode;
  slotProps?: {
    logo?: LogoProps;
  };
};

export function AnimateLogoZoom({ logo, slotProps, className, ...other }: AnimateLogoProps) {
  return (
    <div
      className={twMerge(
        'w-[120px] h-[120px] items-center relative inline-flex justify-center',
        className
      )}
      {...other}
    >
      <m.span
        animate={{ scale: [1, 0.9, 0.9, 1, 1], opacity: [1, 0.48, 0.48, 1, 1] }}
        transition={{
          duration: 2,
          repeatDelay: 1,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        {logo ?? (
          <Logo
            disabled
            {...slotProps?.logo}
            className={twMerge('w-16 h-16', slotProps?.logo?.className)}
          />
        )}
      </m.span>

      <m.span
        className="absolute w-[calc(100%-20px)] h-[calc(100%-20px)] border-3 border-solid border-primary/25"
        animate={{
          scale: [1.6, 1, 1, 1.6, 1.6],
          rotate: [270, 0, 0, 270, 270],
          opacity: [0.25, 1, 1, 1, 0.25],
          borderRadius: ['25%', '25%', '50%', '50%', '25%'],
        }}
        transition={{ ease: 'linear', duration: 3.2, repeat: Infinity }}
      />

      <m.span
        className="w-full h-full absolute border-8 border-solid border-primary/25"
        animate={{
          scale: [1, 1.2, 1.2, 1, 1],
          rotate: [0, 270, 270, 0, 0],
          opacity: [1, 0.25, 0.25, 0.25, 1],
          borderRadius: ['25%', '25%', '50%', '50%', '25%'],
        }}
        transition={{ ease: 'linear', duration: 3.2, repeat: Infinity }}
      />
    </div>
  );
}

export function AnimateLogoRotate({ logo, className, slotProps, ...other }: AnimateLogoProps) {
  return (
    <div
      className={twMerge('w-24 h-24 items-center relative inline-flex justify-center', className)}
      {...other}
    >
      {logo ?? (
        <Logo
          {...slotProps?.logo}
          className={twMerge('z-[9] w-10 h-10', slotProps?.logo?.className)}
        />
      )}

      <m.span
        className="w-full h-full opacity-[0.16] rounded-full absolute bg-gradient-to-br from-transparent from-50% to-primary to-100% transition-opacity duration-200 ease-in-out"
        animate={{ rotate: 360 }}
        transition={{ duration: 10, ease: 'linear', repeat: Infinity }}
      />
    </div>
  );
}

export function AnimateLogoZoomCSS({ logo, slotProps, className }: AnimateLogoProps) {
  return (
    <div
      className={twMerge(
        'w-[120px] h-[120px] relative flex items-center justify-center',
        className
      )}
    >
      {/* Центральный логотип */}
      <div className={twMerge('w-16 h-16 flex items-center justify-center', 'animate-pulse-logo')}>
        {logo ?? <Logo className="w-16 h-16" disabled {...slotProps?.logo} />}
      </div>

      {/* Первый вращающийся спан */}
      <div className="absolute w-[calc(100%-20px)] h-[calc(100%-20px)] border-3 border-primary/25 rounded-xl animate-rotate-scale" />

      {/* Второй вращающийся спан */}
      <div className="absolute w-full h-full border-8 border-primary/25 rounded-xl animate-scale-rotate" />
    </div>
  );
}
